import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngrx/store';
import {
  websiteActions,
  websitePagesActions,
} from 'src/app/shared/store/_features/website/website.actions';
import { selectWebsitePages } from 'src/app/shared/store/_features/website/website.selector';
import { AccountSetupHelperService } from '../account-setup-helper.service';
import { ApiService, AuthenticationService } from 'src/app/shared/services';
import { Subscription, lastValueFrom, take } from 'rxjs';
import {
  StepDefinition,
  StepProgressRunnerComponent,
} from 'src/app/shared/partials/ai/step-progress-runner/step-progress-runner.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-completion-step',
  templateUrl: './completion-step.component.html',
  styleUrls: ['./completion-step.component.css'],
})
export class CompletionStepComponent implements OnInit, OnDestroy {
  @ViewChild(StepProgressRunnerComponent) stepRunner?: StepProgressRunnerComponent;

  subscriptions: Subscription[] = [];
  private homePageSub?: Subscription;
  isHomePageDone = false;
  /**
   * True when the backend reported processState='failed'.
   * Signals step runner to stop spinning and displays the retry block.
   */
  isHomePageFailed = false;
  environment = environment;

  isComplete = false;
  isEvaluatingResume = true;
  isNavigating = false;
  resumeFromStepId?: string;
  private _generationStarterTriggered = false;

  SETUP_STEPS: StepDefinition[] = [
    {
      id: 'domain-setup',
      label: 'Setting up your domain',
      action: () => this.runSetupAndMarkProcessing(),
      isDone: () => true,
    },
    {
      id: 'deploy-website',
      label: 'Deploying your Website',
      action: () => {},
      isDone: () => true,
    },
    {
      id: 'create-home-page',
      label: 'Creating your Home Page',
      action: () => {},
      isDone: () => true,
    },
    {
      id: 'craft-home-content',
      label: 'Crafting your Home Page Content',
      action: () => this.listenToHomePageGenerationValidation(),
      isDone: () => {
        if (!this.isHomePageDone) {
          this.reloadPages();
        }
        return this.isHomePageDone;
      },
      isFailed: () => this.isHomePageFailed,
    },
    {
      id: 'finalize-setup',
      label: 'Finalizing Setup...',
      action: () => {
        this.reloadWebsite();
      },
      isDone: () => true,
    },
  ];

  private _pagePollInFlight = false;

  constructor(
    private store: Store,
    public accountSetupHelperService: AccountSetupHelperService,
    private apiService: ApiService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private toastr: ToastrService,
  ) {}

  get hubName(): string {
    return (
      this.accountSetupHelperService?.wizardData?.hubInput?.hubIdentity?.name?.trim() ||
      'Your Hub'
    );
  }

  async ngOnInit(): Promise<void> {
    await this.evaluateInitialStage();
  }

  /**
   * Determines the starting stage grounded in true BACKEND state.
   * Prevents re-running step 0's setup action on any refresh.
   */
  async evaluateInitialStage(): Promise<void> {
    this.isEvaluatingResume = true;

    // 8-second evaluation timeout so slow/failed network never freezes UI
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Backend evaluation timed out')), 8000),
    );

    try {
      // Edge Case 1: website_setup is already complete on user record
      const currentUser = this.authService.currentUserValue;
      if (Boolean(currentUser?.onBoarding?.websiteSetting)) {
        console.log('[CompletionStep] website_setup is already complete. Navigating to dashboard.');
        this.router.navigate(['/dashboard']);
        return;
      }

      const savedGen = this.accountSetupHelperService.wizardData.generation;
      const identitySubmitted = !!savedGen?.identitySubmitted;

      console.log(
        `[CompletionStep] Evaluated resume: identitySubmitted=${identitySubmitted}, savedGenStatus=${savedGen?.status}`,
      );

      // 3. Resume evaluation: resumeFromStep = 3 ONLY if identitySubmitted === true.
      // Otherwise always start at step 0, even if a website/home page already exists.
      if (!identitySubmitted) {
        console.log('[CompletionStep] Identity not submitted yet. Starting at step 0.');
        this.resumeFromStepId = 'domain-setup';
        this.isEvaluatingResume = false;
        this.cdr.markForCheck();
        return;
      }

      // 4. If identitySubmitted is true: check backend home page state
      const [pagesRes] = await Promise.race([
        Promise.allSettled([
          lastValueFrom(this.apiService.getWebsitePages()),
        ]),
        timeoutPromise,
      ]);

      const pages = pagesRes.status === 'fulfilled' ? pagesRes.value?.pages || [] : [];
      const homePage = pages.find((p: any) => p.slug === 'home');
      const homeState = homePage?.processState;

      // home page 'completed' -> show celebration screen (do not redirect)
      if (homeState === 'completed' || savedGen?.status === 'completed') {
        console.log('[CompletionStep] Home page completed. Showing celebration screen.');
        this.isHomePageDone = true;
        this.isHomePageFailed = false;
        this.isComplete = true;
        this.isEvaluatingResume = false;
        this.accountSetupHelperService.wizardData.generation = {
          status: 'completed',
          startedAt: savedGen?.startedAt ?? Math.floor(Date.now() / 1000),
          identitySubmitted: true,
        };
        this.cdr.markForCheck();
        return;
      }

      // 'failed' -> Retry UI
      if (homeState === 'failed' || savedGen?.status === 'failed') {
        console.warn('[CompletionStep] Home page state is failed. Showing retry UI.');
        this.isHomePageFailed = true;
        this.isEvaluatingResume = false;
        this.cdr.markForCheck();
        return;
      }

      // otherwise poll only (resume at step 3)
      console.log('[CompletionStep] Resuming directly on craft-home-content (step 3, poll only).');
      this.resumeFromStepId = 'craft-home-content';
      this.isEvaluatingResume = false;
      this.cdr.markForCheck();
    } catch (error) {
      console.warn('[CompletionStep] State evaluation error or timeout, applying robust fallback:', error);
      const savedGen = this.accountSetupHelperService.wizardData.generation;
      const identitySubmitted = !!savedGen?.identitySubmitted;
      if (!identitySubmitted) {
        this.resumeFromStepId = 'domain-setup';
        this.isEvaluatingResume = false;
        this.cdr.markForCheck();
        return;
      }
      if (savedGen?.status === 'completed') {
        this.isHomePageDone = true;
        this.isComplete = true;
        this.isEvaluatingResume = false;
        this.cdr.markForCheck();
        return;
      }
      if (savedGen?.status === 'failed') {
        this.isHomePageFailed = true;
        this.isEvaluatingResume = false;
        this.cdr.markForCheck();
        return;
      }
      this.resumeFromStepId = 'craft-home-content';
      this.isEvaluatingResume = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Step 0 action: await the real HTTP success of identity + subdomain,
   * then set identitySubmitted = true and await the progress save. On failure show Retry.
   */
  async runSetupAndMarkProcessing(): Promise<void> {
    console.log('[CompletionStep] Starting domain setup...');
    try {
      this._generationStarterTriggered = true;
      // 1. Await real HTTP success of identity + subdomain
      await this.accountSetupHelperService.updateWebsiteIdentity();
      // 2. Set identitySubmitted = true and await progress save
      this.accountSetupHelperService.wizardData.generation = {
        status: 'processing',
        startedAt: Math.floor(Date.now() / 1000),
        identitySubmitted: true,
      };
      await this.accountSetupHelperService.saveProgressAsync(10, 'completion');
    } catch (err) {
      console.error('[CompletionStep] Step 0 setup failed:', err);
      this.isHomePageFailed = true;
      this.accountSetupHelperService.wizardData.generation.status = 'failed';
      this.accountSetupHelperService.flushSaveProgress();
      this.cdr.markForCheck();
      throw err;
    }
  }

  listenToHomePageGenerationValidation() {
    // Avoid stacking duplicate subscriptions if the action runs more than once
    this.homePageSub?.unsubscribe();
    this.homePageSub = this.store.select(selectWebsitePages).subscribe((pages) => {
      this._pagePollInFlight = false;
      const homePage = pages.find((p) => p.slug === 'home');
      if (homePage?.processState === 'completed') {
        console.log('[CompletionStep] Home page generation completed.');
        this.isHomePageDone = true;
        this.isHomePageFailed = false;
      } else if (homePage?.processState === 'failed') {
        console.warn('[CompletionStep] Home page generation failed — stopping runner.');
        this.isHomePageFailed = true;
      }
    });
    this.subscriptions.push(this.homePageSub);
  }

  reloadWebsite() {
    this.store.dispatch(websiteActions.load());
  }

  reloadPages() {
    if (this._pagePollInFlight) {
      return;
    }
    this._pagePollInFlight = true;
    this.store.dispatch(websitePagesActions.load({ withOutScreenshots: true }));
  }

  handleStepTimeout(event: { stepIndex: number; step: StepDefinition }) {
    console.warn('[CompletionStep] Step timed out:', event?.step?.label);
    this.isHomePageFailed = true;
    this.accountSetupHelperService.wizardData.generation.status = 'failed';
    this.accountSetupHelperService.flushSaveProgress();
  }

  handleStepFailed(event: { stepIndex: number; step: StepDefinition }) {
    console.error('[CompletionStep] Step failed:', event?.step?.label);
    this.isHomePageFailed = true;
    this.accountSetupHelperService.wizardData.generation.status = 'failed';
    this.accountSetupHelperService.flushSaveProgress();
  }

  /**
   * Called when all steps in the progress runner finish.
   * MUST NOT mark onboarding complete or show completion if homepage generation failed.
   */
  async onAllDone() {
    console.log('[CompletionStep] onAllDone called. isHomePageDone:', this.isHomePageDone);
    if (!this.isHomePageDone) {
      console.warn('[CompletionStep] onAllDone ignored because homepage generation has not succeeded.');
      return;
    }
    this.isComplete = true;
    this.isEvaluatingResume = false;
    this.accountSetupHelperService.wizardData.generation = {
      status: 'completed',
      startedAt: this.accountSetupHelperService.wizardData.generation.startedAt,
      identitySubmitted: true,
    };
    await this.accountSetupHelperService.saveProgressAsync(10, 'completion');
    this.cdr.markForCheck();
  }

  /**
   * Manual navigation to dashboard (only rendered on completion screen).
   */
  async navigateToDashboard() {
    console.log('[CompletionStep] Navigating to dashboard.');
    if (this.isNavigating) return;
    this.isNavigating = true;
    this.cdr.markForCheck();

    try {
      await this.accountSetupHelperService.completeAccountSetup();
      if (this.accountSetupHelperService.wizardModel) {
        this.accountSetupHelperService.wizardModel.goToNextStep();
      }
      this.router.navigate(['/dashboard']);
    } catch (err: any) {
      console.error('[CompletionStep] Error in completeAccountSetup during navigation:', err);
      this.toastr.error(
        err?.error?.message || err?.message || 'Failed to complete setup. Please try again.',
      );
      this.isNavigating = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * Retries AI content generation after a failure or timeout.
   * Safety check: does NOT start a second generation if processState is already 'processing' or 'completed'.
   * Only re-triggers updateWebsiteIdentity() if processState is 'failed' or not yet started.
   */
  async retryGeneration() {
    console.log('[CompletionStep] Retrying generation...');

    // Check latest home page state
    let currentState: string | undefined;
    try {
      const pages = await lastValueFrom(this.store.select(selectWebsitePages).pipe(take(1)));
      const homePage = pages?.find((p) => p.slug === 'home');
      currentState = homePage?.processState;
    } catch {
      // If store read fails, continue to fallback check
    }

    if (currentState === 'completed') {
      console.log('[CompletionStep] Home page is already completed. Advancing runner without re-generating.');
      this.isHomePageDone = true;
      this.isHomePageFailed = false;
      this.stepRunner?.retryCurrentStep();
      return;
    }

    if (currentState === 'processing') {
      console.log('[CompletionStep] Home page is currently processing. Resuming polling without restarting generation.');
      this.isHomePageFailed = false;
      this.isHomePageDone = false;
      this._pagePollInFlight = false;
      this.stepRunner?.retryCurrentStep();
      return;
    }

    console.log('[CompletionStep] Home page state is', currentState, '- restarting generation.');
    this.isHomePageFailed = false;
    this.isHomePageDone = false;
    this._pagePollInFlight = false;
    try {
      this.accountSetupHelperService.wizardData.generation = {
        status: 'processing',
        startedAt: Math.floor(Date.now() / 1000),
        identitySubmitted: true,
      };
      await this.accountSetupHelperService.saveProgressAsync(10, 'completion');
      await this.accountSetupHelperService.updateWebsiteIdentity();
    } catch (err) {
      console.error('[CompletionStep] updateWebsiteIdentity failed during retry:', err);
    }
    this.stepRunner?.retryCurrentStep();
  }

  ngOnDestroy(): void {
    this._pagePollInFlight = false;
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}